import { CheckCircle2 } from 'lucide-react';

import { Avatar, Badge, Button, ConfirmButton, useToast } from '@/components/ui';
import { useApproveUser, usePendingUsers, useRejectUser } from '@/hooks/useUsers';
import { errorMessage } from '@/utils/apiError';
import cn from '@/utils/cn';
import { formatDate } from '@/utils/format';

const columns = ['Name', 'Mobile', 'Email', 'Requested role', 'Submitted', 'Action'];
const TH =
  'whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-ink/40';
const TD = 'whitespace-nowrap px-5 py-3.5 text-sm';

const ApprovalsPage = () => {
  const { toast } = useToast();
  const { data: pending, isLoading, isError, isFetching } = usePendingUsers();
  const approve = useApproveUser();
  const reject = useRejectUser();

  const onApprove = (id: number, name: string) => {
    approve.mutate(id, {
      onSuccess: () => toast.success(`${name} approved`),
      // Surface the server's own message: an Admin request whose mobile is not
      // one of the authorised admin numbers is refused with an explanation the
      // approver has to read, not a generic retry prompt.
      onError: (error) => toast.error(errorMessage(error, 'Could not approve. Please try again.')),
    });
  };

  const onReject = (id: number, name: string) => {
    reject.mutate(id, {
      onSuccess: () => toast.info(`${name} rejected`),
      onError: (error) => toast.error(errorMessage(error, 'Could not reject. Please try again.')),
    });
  };

  const rows = pending ?? [];

  const header = (
    <>
      <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
        Pending approvals
      </h1>
      <p className="mt-1.5 text-sm text-ink/60">
        Review and approve people requesting access to the workspace.
      </p>
    </>
  );

  if (!isLoading && !isError && rows.length === 0) {
    return (
      <div>
        {header}
        <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-line bg-white py-16 shadow-sm">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-success-tint text-success">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <p className="mt-4 text-sm font-semibold text-ink">All caught up</p>
          <p className="mt-1 text-sm text-ink/50">No pending registrations right now.</p>
        </div>
      </div>
    );
  }

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: 4 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={i}>
          <td colSpan={columns.length} className="px-5 py-3.5">
            <div className="h-4 w-full max-w-md animate-pulse rounded bg-line/50" aria-hidden />
            <span className="sr-only">Loading</span>
          </td>
        </tr>
      ));
    }

    if (isError) {
      return (
        <tr>
          <td colSpan={columns.length} className="px-5 py-12 text-center text-sm text-danger">
            Failed to load pending registrations. Please try again.
          </td>
        </tr>
      );
    }

    return rows.map((user) => {
      const busy =
        (approve.isPending && approve.variables === user.id) ||
        (reject.isPending && reject.variables === user.id);
      return (
        <tr key={user.id} className="transition-colors hover:bg-cream/50">
          <td className={TD}>
            <div className="flex items-center gap-3">
              <Avatar name={user.name} size="sm" />
              <span className="font-semibold text-ink">{user.name}</span>
            </div>
          </td>
          <td className={`${TD} text-ink/60`}>+91 {user.mobile}</td>
          <td className={`${TD} text-ink/60`}>{user.email}</td>
          <td className={TD}>
            <Badge variant="info">{user.jobRole ?? '-'}</Badge>
          </td>
          <td className={`${TD} text-ink/60`}>{formatDate(user.createdAt)}</td>
          <td className={TD}>
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => onApprove(user.id, user.name)}>
                Approve
              </Button>
              <ConfirmButton
                variant="secondary"
                size="sm"
                disabled={busy}
                onConfirm={() => onReject(user.id, user.name)}
              >
                Reject
              </ConfirmButton>
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <div>
      {header}
      <div className="mt-6 overflow-hidden rounded-xl border border-line bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="text-sm font-bold text-ink">Awaiting review</h2>
          {!isLoading && <Badge variant="warning">{rows.length} pending</Badge>}
        </div>
        <div
          className={cn(
            'overflow-x-auto transition-opacity',
            isFetching && !isLoading && 'opacity-60',
          )}
        >
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-line bg-cream/60">
                {columns.map((col) => (
                  <th key={col} className={TH}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">{renderBody()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ApprovalsPage;
